import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Box } from 'components';
import createComponent, { ComponentRuleProps, RuleStyles } from 'styles/fela/createComponent';
import { withBasePadding } from 'styles/fela/mixins';
import { useTheme } from 'server/theme/hooks';
import { LogoImage } from '../AppView/Header/Styles';

type ProfilesLayoutProps = {
  children?: ReactNode;
  hideInitially?: boolean;
};

type AvatarImageContainerProps = {
  size: string;
  hover: boolean;
};

export const LogoLink = createComponent(({ theme }: ComponentRuleProps): RuleStyles => ({
  marginRight: theme.margin.small,
  display: 'flex',
  height: theme.header.logo.height,
  width: theme.header.logo.width,
  alignItems: 'center',
  flexShrink: 0,
}));

export const HeaderContainer = createComponent(({ theme }: ComponentRuleProps): RuleStyles => ({
  color: theme.header.textColor,
  height: theme.header.height,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.header,
  alignItems: 'center',
  display: 'flex',

  extend: [
    { condition: true, style: withBasePadding() },
  ],
}));

export const AvatarContainer = createComponent((
  { size = '0px', hover, theme }: ComponentRuleProps & Partial<AvatarImageContainerProps>,
): RuleStyles => ({
  width: size,
  ...(hover ? {
    cursor: 'pointer',
    ':hover div:first-child': { boxShadow: theme.shadow.avatarImageHover },
    ':hover .editCircle circle': { fill: theme.color.primary },
    ':hover .editCircle path': { fill: theme.color.editIconHover },
  } : {}),
}), 'div', ['size', 'hover'], true);

export const AvatarImageContainer = createComponent((
  { size = '0px', theme }: ComponentRuleProps & Partial<AvatarImageContainerProps>,
): RuleStyles => ({
  position: 'relative',
  width: size,
  height: size,
  borderRadius: size,
  backgroundColor: theme.color.imageBackground,
}), 'div', ['size', 'hover'], true);

export const EditOverlay = createComponent((): RuleStyles => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: '35%',
  height: '35%',
}), 'div');

export const AvatarImagePicture = createComponent((
  { hidden }: ComponentRuleProps & { hidden?: boolean },
) => ({
  borderRadius: '50%',
  width: '100%',
  height: '100%',
  objectFit: 'cover' as const,
  opacity: hidden ? 0 : 1,
  transition: 'opacity 200ms ease-out',
}), 'img', ['hidden']);

export const OverlayContainer = createComponent(({ theme }: ComponentRuleProps): RuleStyles => ({
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: theme.zIndex.modal,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.color.page,
  userSelect: 'none' as const,
  opacity: 0,
  animationName: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  } as any,
  animationDuration: '200ms',
  animationTimingFunction: 'ease-in',
  animationFillMode: 'forwards',
}));

export const SpinnerContainer = createComponent(({ theme }: ComponentRuleProps): RuleStyles => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.color.primary,
}));

export function ProfilesLayout({ children, hideInitially = false }: ProfilesLayoutProps) {
  const theme = useTheme();
  const {
    brandName,
    headerLogo,
  } = useSelector((state: any) => ({
    brandName: state.settings.brand.name,
    headerLogo: state.settings.brand.headerLogo,
  }), shallowEqual);

  const [isVisible, setIsVisible] = useState(!hideInitially);

  useEffect(() => {
    if (hideInitially) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hideInitially]);

  return (
    <Box
      column
      fullWidth
    >
      <HeaderContainer>
        <LogoLink>
          <LogoImage
            src={headerLogo}
            alt={brandName}
          />
        </LogoLink>
      </HeaderContainer>

      <Box
        p="xxlarge"
        pt={theme.header.height}
        sm-px="large"
        xs-px="large"
        xs-m="0"
        justifyContent="flex-start"
        column
        fullWidth
        visibility={isVisible ? 'visible' : 'hidden'}
      >
        {children}
      </Box>
    </Box>
  );
}

